import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs';
import FriendlyPhrase from '../../components/FriendlyPhrase/FriendlyPhrase';
import PhoneInput from '../../components/PhoneInput/PhoneInput';
import { useAuth } from '../../contexts/AuthContext';
import { useLoader } from '../../contexts/LoaderContext';
import { useToast } from '../../contexts/ToastContext';
import { useEmpresaService } from '../../services/empresaServices';
import { useParticipanteService } from '../../services/participanteServices';
import { useTurmaService } from '../../services/turmaServices';
import { createEmptyPhoneModel, parseCanonicalPhone, phoneModelFromApiValue, toPhonePayload } from '../../utils/phoneCountries';
import styles from './participanteformpage.module.css';

function ParticipanteFormPage() {
  const { logout } = useAuth();
  const { showToast } = useToast();
  const { showLoader, hideLoader } = useLoader();
  const navigate = useNavigate();
  const { idEmpresa, idTurma, idParticipante } = useParams();
  const { pathname } = useLocation();
  const { obterEmpresaPorId } = useEmpresaService();
  const { obterTurmaPorId } = useTurmaService();
  const { obterParticipantePorId, criarParticipante, atualizarParticipante } = useParticipanteService();
  const isEditing = Boolean(idParticipante && pathname.includes('atualizar'));
  const [empresa, setEmpresa] = useState(null);
  const [turma, setTurma] = useState(null);
  const [celularErro, setCelularErro] = useState('');
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    celular: createEmptyPhoneModel(),
    senha: '',
  });

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setCelularErro('');
    if (!formData.celular.isValid) {
      const message = 'Celular inválido para o país informado.';
      setCelularErro(message);
      showToast(message, 3000, 'error');
      return;
    }

    showLoader(isEditing ? 'Atualizando participante' : 'Criando participante');
    try {
      const participanteJson = {
        nome: formData.nome,
        email: formData.email,
        senha: formData.senha,
        loginPor: 'celular',
        idTurma,
        ...toPhonePayload('celular', formData.celular),
      };
      if (isEditing) participanteJson.id = idParticipante;

      const response = isEditing
        ? await atualizarParticipante(participanteJson)
        : await criarParticipante(participanteJson);

      if (response.success) {
        if (response.celular) {
          setFormData((prev) => ({
            ...prev,
            celular: parseCanonicalPhone(response.celular) || prev.celular,
          }));
        }
        showToast(
          isEditing ? 'Participante atualizado com sucesso!' : 'Participante criado com sucesso!',
          3000,
          'success'
        );
        navigate(`/empresas/${idEmpresa}/${idTurma}`);
        return;
      }

      const phoneError = response.code === 'invalid_celular'
        ? 'Celular inválido para o país informado.'
        : response.code === 'duplicate_celular'
          ? 'Este celular já está cadastrado.'
          : '';
      if (phoneError) setCelularErro(phoneError);
      showToast(phoneError || response.message || 'Erro ao salvar participante.', 3000, 'error');
      if (response.status === 401 || response.message?.toLowerCase().includes('token')) logout();
    } catch {
      showToast('Erro de rede ou servidor.', 3000, 'error');
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    const carregarDados = async () => {
      showLoader('Carregando dados');
      try {
        const empresaResp = await obterEmpresaPorId(idEmpresa);
        if (!empresaResp.success) throw new Error(empresaResp.message || 'Erro ao carregar empresa');
        setEmpresa(empresaResp.data);

        const turmaResp = await obterTurmaPorId(idTurma);
        if (!turmaResp.success) throw new Error(turmaResp.message || 'Erro ao carregar turma');
        setTurma(turmaResp.data);

        if (isEditing) {
          const participanteResp = await obterParticipantePorId(idParticipante);
          if (!participanteResp.success) {
            throw new Error(participanteResp.message || 'Erro ao carregar participante');
          }
          const participante = participanteResp.data;
          setFormData({
            nome: participante.nome || '',
            email: participante.email || '',
            celular: phoneModelFromApiValue(participante.celular),
            senha: '',
          });
        }
      } catch (error) {
        showToast(error.message || 'Erro ao carregar dados', 3000, 'error');
        if (error.message?.toLowerCase().includes('token')) logout();
      } finally {
        hideLoader();
      }
    };

    if (idEmpresa && idTurma) carregarDados();
  }, [idEmpresa, idTurma, idParticipante, isEditing]);

  const breadcrumbIds = [];
  if (empresa) breadcrumbIds.push({ id: idEmpresa, label: empresa.nome, place: 1 });
  if (turma) breadcrumbIds.push({ id: idTurma, label: turma.nome, place: 2 });
  if (isEditing && formData.nome) breadcrumbIds.push({ id: idParticipante, label: formData.nome, place: 3 });

  return (
    <div className={styles.ParticipanteFormPage}>
      <div className={styles.containerTop}>
        <Breadcrumbs idLabels={breadcrumbIds} />
        <h1 className={styles.title}>{isEditing ? 'Editar Participante' : 'Cadastro de Participante'}</h1>
        <FriendlyPhrase page={isEditing ? 'atualizarParticipante' : 'adicionarParticipante'} />
      </div>
      <form onSubmit={handleSubmit} className={styles.formulario}>
        <label>Nome:
          <input type="text" name="nome" value={formData.nome} onChange={handleChange} required />
        </label>
        <label>E-mail:
          <input type="email" name="email" value={formData.email} onChange={handleChange} required />
        </label>
        <PhoneInput
          name="celular"
          value={formData.celular}
          onChange={(celular) => setFormData((prev) => ({ ...prev, celular }))}
          label="Celular:"
          error={celularErro}
          required
        />
        <label>Senha:
          <input
            type="password"
            name="senha"
            value={formData.senha}
            onChange={handleChange}
            required={!isEditing}
            placeholder={isEditing ? 'Preencha apenas se quiser alterar' : ''}
          />
        </label>
        <div className={styles.submitArea}>
          <button type="submit" className={styles.submitButton}>
            {isEditing ? 'Atualizar Participante' : 'Salvar Participante'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ParticipanteFormPage;
